import React, { useState, useEffect, useCallback } from 'react'
import { Button, Space, Row, Col, message, Empty, Modal, Form, Input, InputNumber } from 'antd'
import { AppCard, PageLoading } from '@/shared/components/ui'
import { PlusOutlined, ReloadOutlined } from '@ant-design/icons'
import { isAxiosError } from 'axios'
import { useAuthStore } from '../../stores/authStore'
import { isAdminRole } from '@/shared/utils/roleUtils'
import { humanAdminApi, HumanResponse, HumanCreate } from '../../shared/api/humanApi'
import HumanList from './HumanList'
import { useI18n } from '@/shared/i18n'

const UserManagement: React.FC = () => {
  const { user: currentUser } = useAuthStore()
  const [humans, setHumans] = useState<HumanResponse[]>([])
  const [load, setLoad] = useState<Record<number, number>>({})
  const [loading, setLoading] = useState(true)
  const [showCreate, setShowCreate] = useState(false)
  const [form] = Form.useForm()
  const { t } = useI18n('modules')

  const fetchData = useCallback(async () => {
    setLoading(true)
    try {
      const [humanList, loadList] = await Promise.all([
        humanAdminApi.list(),
        humanAdminApi.getLoad(),
      ])
      setHumans(humanList)
      const loadMap: Record<number, number> = {}
      loadList.forEach(l => { loadMap[l.id] = l.current_sessions })
      setLoad(loadMap)
    } catch {
      message.error(t('userManagement.fetchFailed'))
    } finally {
      setLoading(false)
    }
  }, [currentUser])

  useEffect(() => {
    if (isAdminRole(currentUser?.role || '')) {
      fetchData()
    } else if (currentUser !== null) {
      setLoading(false)
    }
  }, [currentUser, fetchData])

  const handleCreate = async () => {
    try {
      const values = await form.validateFields() as HumanCreate
      await humanAdminApi.create(values)
      message.success(t('userManagement.createSuccess'))
      setShowCreate(false)
      form.resetFields()
      fetchData()
    } catch (e) {
      if (isAxiosError(e)) {
        if (e.response?.status === 409) message.error(t('userManagement.usernameExists'))
        else message.error(t('userManagement.createFailed'))
      }
      // antd form validation error: e.errorFields exists, silently ignore
    }
  }

  if (!isAdminRole(currentUser?.role || '')) {
    return <AppCard><Empty description={t('userManagement.noPermission')} /></AppCard>
  }

  return (
    <div>
      <AppCard title={t('userManagement.title')} style={{ marginBottom: 16 }}>
        <Row justify="end">
          <Col>
            <Space>
              <Button type="primary" icon={<PlusOutlined />} onClick={() => setShowCreate(true)}>
                {t('userManagement.create')}
              </Button>
              <Button icon={<ReloadOutlined />} onClick={fetchData} loading={loading}>
                {t('userManagement.refresh')}
              </Button>
            </Space>
          </Col>
        </Row>
      </AppCard>

      <AppCard>
        {loading ? (
          <PageLoading />
        ) : (
          <HumanList humans={humans} load={load} onRefresh={fetchData} />
        )}
      </AppCard>

      <Modal
        title={t('userManagement.createModal.title')}
        open={showCreate}
        onOk={handleCreate}
        onCancel={() => { setShowCreate(false); form.resetFields() }}
        okText={t('userManagement.createModal.ok')}
        cancelText={t('userManagement.createModal.cancel')}
      >
        <Form form={form} layout="vertical" initialValues={{ max_sessions: 5 }}>
          <Form.Item name="username" label={t('userManagement.form.username')} rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="password" label={t('userManagement.form.password')} rules={[{ required: true, min: 8 }]}>
            <Input.Password />
          </Form.Item>
          <Form.Item name="display_name" label={t('userManagement.form.displayName')} rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="max_sessions" label={t('userManagement.form.maxSessions')}>
            <InputNumber min={1} max={20} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}

export default UserManagement
